import React, { forwardRef } from "react";
import { ChevronRightIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { textVariants } from "./text";

export type TableProps = {
	children: React.ReactNode;
	className?: string;
} & React.HTMLAttributes<HTMLTableElement>;

const TableComponent = forwardRef<HTMLTableElement, TableProps>(
	({ children, className, ...props }, ref) => (
		<table ref={ref} className={cn("w-full", className)} {...props}>
			{children}
		</table>
	),
);
TableComponent.displayName = "Table";

export type TableHeaderProps = {
	children: React.ReactNode;
} & React.HTMLAttributes<HTMLTableSectionElement>;

const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
	({ children, className, ...props }, ref) => (
		<thead
			ref={ref}
			className={cn("table w-full table-fixed", className)}
			{...props}
		>
			<tr className="border-muted h-12 border-b bg-neutral-50">{children}</tr>
		</thead>
	),
);
TableHeader.displayName = "TableHeader";

export type TableColumnProps = {
	children?: React.ReactNode;
	className?: string;
	sortable?: boolean;
	sorted?: boolean;
	sortDirection?: "ASC" | "DESC";
	/** When true, suppresses the built-in sort icon rendering. Useful when the consumer wants to render their own sort icon. */
	renderSortIcon?: boolean;
} & React.ThHTMLAttributes<HTMLTableHeaderCellElement>;

const TableColumn = forwardRef<HTMLTableHeaderCellElement, TableColumnProps>(
	(
		{
			children,
			className,
			sortable,
			sorted,
			sortDirection,
			renderSortIcon = true,
			...props
		},
		ref,
	) => (
		<th
			ref={ref}
			scope="col"
			className={cn(
				"py-3 pl-1.5 pr-1.5 first:pl-3 last:pr-3",
				"lg:pl-2 lg:pr-2 lg:first:pl-4 lg:last:pr-4",
				textVariants({ variant: "body-xs" }),
				"text-color-body-muted text-left",
				sortable && "cursor-pointer",
				className,
			)}
			{...props}
		>
			{sortable && renderSortIcon ? (
				<div className="inline-table relative">
					{children}
					<div className="ml-[6px] mr-2 inline-table absolute top-1/2 -right-6 transform -translate-y-1/2">
						<ChevronRightIcon
							className={cn(
								"size-2.5 transition-all duration-150",
								sorted
									? [
											"text-color-body-muted",
											sortDirection === "ASC" && "rotate-90",
											sortDirection === "DESC" && "rotate-[270deg]",
										]
									: "rotate-[270deg] text-neutral-300",
							)}
						/>
					</div>
				</div>
			) : (
				children
			)}
		</th>
	),
);
TableColumn.displayName = "TableColumn";

export type TableBodyProps = {
	children: React.ReactNode;
	className?: string;
} & React.HTMLAttributes<HTMLTableSectionElement>;

const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
	({ children, className, ...props }, ref) => (
		<tbody
			ref={ref}
			className={cn("divide-muted block h-full divide-y", className)}
			{...props}
		>
			{children}
		</tbody>
	),
);
TableBody.displayName = "TableBody";

export type TableRowProps = {
	children: React.ReactNode;
	className?: string;
	height?: "sm" | "md" | "lg";
	interactive?: boolean;
	wrapper?: (children: React.ReactNode) => React.ReactNode;
} & React.ComponentProps<"tr">;

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
	(
		{ children, className, height = "sm", interactive, wrapper, ...props },
		ref,
	) => {
		const trElement = (
			<tr
				ref={ref}
				className={cn(
					"table w-full table-fixed group/table-row",
					height === "sm" && "h-12",
					height === "md" && "h-16",
					height === "lg" && "h-24",
					interactive &&
						"cursor-pointer hover:bg-neutral-50 data-[state=open]:bg-neutral-50 transition-colors align-middle",
					className,
				)}
				{...props}
			>
				{children}
			</tr>
		);
		return wrapper ? <>{wrapper(trElement)}</> : trElement;
	},
);
TableRow.displayName = "TableRow";

export type TableCellProps = {
	children: React.ReactNode;
	className?: string;
} & React.TdHTMLAttributes<HTMLTableCellElement>;

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
	({ children, className, ...props }, ref) => (
		<td
			ref={ref}
			className={cn(
				"py-3 pl-1.5 pr-1.5 first:pl-3 last:pr-3",
				"lg:py-4 lg:pl-2 lg:pr-2 lg:first:pl-4 lg:last:pr-4",
				textVariants({ variant: "body-xs" }),
				"text-color-body align-middle",
				className,
			)}
			{...props}
		>
			{children}
		</td>
	),
);
TableCell.displayName = "TableCell";

export type TableFooterProps = {
	children: React.ReactNode;
	className?: string;
} & React.HTMLAttributes<HTMLTableSectionElement>;

const TableFooter = forwardRef<HTMLTableSectionElement, TableFooterProps>(
	({ children, className, ...props }, ref) => (
		<tfoot
			ref={ref}
			className={cn(
				"table w-full table-fixed border-t border-muted divide-y divide-muted",
				className,
			)}
			{...props}
		>
			{children}
		</tfoot>
	),
);
TableFooter.displayName = "TableFooter";

export const Table = Object.assign(TableComponent, {
	Header: TableHeader,
	Body: TableBody,
	Column: TableColumn,
	Row: TableRow,
	Cell: TableCell,
	Footer: TableFooter,
});
